import React from 'react';
import { Check, PartyPopper } from 'lucide-react';
import { cap, fd, isBD, pd } from './calendarLogic';
import { StatusBadge } from './StatusBadge';

const SECTIONS = [
  { k: 'late', title: 'Pasos atrasados', tone: 'border-l-red-500', head: 'text-red-700' },
  { k: 'now', title: 'Toca hoy', tone: 'border-l-amber-500', head: 'text-amber-700' },
  { k: 'next', title: 'Empieza mañana', tone: 'border-l-indigo-500', head: 'text-indigo-700' },
];

const AlertCard = ({ a, tone, onMark }) => (
  <div data-testid={`alerta-${a.o.id}-${a.r.i}`} className={`bg-white border border-slate-200 border-l-4 ${tone} rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow animate-in fade-in slide-in-from-bottom-1`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="font-bold text-slate-900">{a.o.cliente}</p>
        <p className="text-xs text-slate-500">{a.pl.type.n}</p>
      </div>
      <StatusBadge pl={a.pl} />
    </div>
    <p className="mt-2 text-sm text-slate-700"><span className="font-semibold">{a.r.n}</span> · <span className="font-mono text-xs">{fd(a.r.st)} → {fd(a.r.en)}</span></p>
    <button data-testid={`btn-marcar-listo-${a.o.id}-${a.r.i}`} onClick={() => onMark(a.o.id, a.r.i, true)}
      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold transition-colors">
      <Check size={14} /> Marcar listo
    </button>
  </div>
);

export const HoyTab = ({ alerts, risk, tasks, onDoneTask, hasOrders, onMark, today }) => {
  const now = pd(today);
  return (
    <div data-testid="tab-content-hoy" className="space-y-8">
      <p className="text-sm text-slate-500">{cap(now.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }))}{isBD(now) ? '' : ' · hoy no es día hábil'}</p>
      {!alerts.length && !risk.length && !tasks.length ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
          <PartyPopper className="mx-auto text-emerald-500" size={32} />
          <p className="mt-3 font-semibold text-slate-800">{hasOrders ? 'Todo al día: nada atrasado ni por empezar' : 'Aún no hay pedidos. Crea el primero con "Nuevo pedido".'}</p>
        </div>
      ) : null}
      {risk.length ? (
        <section>
          <h2 className="text-base font-bold text-red-700 mb-3">Entrega en riesgo ({risk.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {risk.map(({ o, pl }) => (
              <div key={o.id} data-testid={`riesgo-${o.id}`} className="bg-red-50/50 border border-red-200 border-l-4 border-l-red-500 rounded-xl p-4">
                <div className="flex justify-between gap-2"><p className="font-bold text-slate-900">{o.cliente}</p><StatusBadge pl={pl} /></div>
                <p className="mt-1 text-sm text-slate-700">Límite <span className="font-mono">{fd(pl.dead)}</span> · Proyección <span className="font-mono">{fd(pl.fc)}</span></p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {tasks.length ? (
        <section>
          <h2 className="text-base font-bold mb-3 text-violet-700">Tareas para hoy o vencidas ({tasks.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {tasks.map(({ o, t }) => (
              <div key={t.id} data-testid={`hoy-task-${t.id}`} className={`bg-white border border-slate-200 border-l-4 ${t.fecha < today ? 'border-l-red-500' : 'border-l-violet-500'} rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow animate-in fade-in slide-in-from-bottom-1`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-slate-900">{o.cliente}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.fecha < today ? 'bg-red-50 text-red-700' : 'bg-violet-50 text-violet-700'}`}>{t.fecha < today ? `Vencida · ${fd(pd(t.fecha))}` : 'Hoy'}</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{t.desc}</p>
                <button data-testid={`btn-hoy-task-done-${t.id}`} onClick={() => onDoneTask(o.id, t.id)} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors"><Check size={14} /> Hecha</button>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {SECTIONS.map(({ k, title, tone, head }) => {
        const L = alerts.filter((a) => a.k === k);
        if (!L.length) return null;
        return (
          <section key={k}>
            <h2 className={`text-base font-bold mb-3 ${head}`}>{title} ({L.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {L.map((a) => <AlertCard key={a.o.id + a.r.i} a={a} tone={tone} onMark={onMark} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
};
